import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import HlsPlayer from "../components/HlsPlayer";
import ChatPanel from "../components/ChatPanel";
import { StormDeskRadar } from "../components/StormDeskRadar";
import { MapPin, Radio, Radar as RadarIcon, Tv } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { SOCIALS } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StreamPlayer = ({ stream }) => {
  if (!stream) return null;
  const kind = (stream.kind || "hls").toLowerCase();
  if (kind === "youtube") {
    let id = stream.src;
    try {
      if (/youtu\.be\//.test(id)) id = id.split("youtu.be/")[1].split(/[?&]/)[0];
      else if (/youtube\.com\/watch/.test(id)) id = new URL(id).searchParams.get("v");
      else if (/youtube\.com\/live\//.test(id)) id = id.split("live/")[1].split(/[?&]/)[0];
    } catch (_) {}
    return (
      <div className="rounded-xl overflow-hidden bg-black border border-white/5 shadow-2xl aspect-video">
        <iframe
          title={stream.title}
          data-testid="watch-youtube-player"
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=0`}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
    );
  }
  if (kind === "twitch") {
    let ch = stream.src;
    try {
      if (/twitch\.tv\//.test(ch)) ch = ch.split("twitch.tv/")[1].split(/[?/]/)[0];
    } catch (_) {}
    const parent = window.location.hostname;
    return (
      <div className="rounded-xl overflow-hidden bg-black border border-white/5 shadow-2xl aspect-video">
        <iframe
          title={stream.title}
          data-testid="watch-twitch-player"
          className="w-full h-full"
          src={`https://player.twitch.tv/?channel=${ch}&parent=${parent}&autoplay=true`}
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      </div>
    );
  }
  return <HlsPlayer src={stream.src} poster={stream.poster} />;
};

const kindLabel = (k) => {
  const m = { hls: "HLS", youtube: "YouTube", twitch: "Twitch", rtmp: "RTMP" };
  return m[(k || "").toLowerCase()] || "LIVE";
};

const StreamCard = ({ s, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`watch-feed-card-${s.id}`}
    className={`w-full text-left group rounded-lg border overflow-hidden transition-colors ${
      active ? "border-red-500/60 bg-red-500/5" : "border-white/5 hover:border-cyan-400/40 bg-white/[0.02]"
    }`}
  >
    <div className="aspect-video relative bg-black">
      {s.poster ? (
        <img src={s.poster} alt="" className="w-full h-full object-cover opacity-90" />
      ) : (
        <div className="w-full h-full grid place-items-center text-slate-500">
          <Radio className="w-8 h-8" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
      <div className="absolute top-2 left-2 flex items-center gap-2">
        {s.live ? (
          <Badge className="bg-red-600 hover:bg-red-600 text-white gap-1.5 border-0 text-[10px]">
            <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE
          </Badge>
        ) : (
          <Badge variant="secondary" className="bg-slate-800 text-slate-300 border-0 text-[10px]">OFFLINE</Badge>
        )}
        <Badge variant="outline" className="border-white/20 text-slate-200 bg-black/40 mono text-[10px]">{kindLabel(s.kind)}</Badge>
      </div>
      <div className="absolute bottom-2 left-2 right-2 text-xs">
        <div className="font-semibold text-slate-100 truncate">{s.title}</div>
        <div className="text-slate-400 truncate">{s.chaser ? s.chaser + " · " : ""}{s.location || ""}</div>
      </div>
    </div>
  </button>
);

const RadarProgram = () => (
  <div data-testid="watch-radar-program">
    <div className="overflow-hidden rounded-xl border border-white/5 shadow-2xl">
      <StormDeskRadar height="max(520px, calc(100vh - 420px))" />
    </div>
    <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <Badge className="bg-red-600 hover:bg-red-600 text-white gap-1.5 border-0 text-[10px]">
            <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE RADAR
          </Badge>
          <Badge variant="outline" className="border-white/20 text-slate-300 bg-black/40 mono text-[10px]">24/7</Badge>
        </div>
        <h2 className="display-italic text-2xl uppercase text-white mt-2">Storm Desk radar broadcast</h2>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Live interactive radar with real-time National Weather Service alerts. Chaser video feeds appear above whenever a
          field unit goes on air.
        </p>
      </div>
      <Link to="/radar" data-testid="watch-open-radar-link">
        <Button variant="outline" className="h-10 gap-2 border-white/15 bg-white/[0.03] hover:bg-white/[0.08] text-slate-100">
          <RadarIcon className="w-4 h-4 text-cyan-400" /> Full radar workspace
        </Button>
      </Link>
    </div>
  </div>
);

const Watch = () => {
  const { id } = useParams();
  const [streams, setStreams] = useState([]);
  const [selectedId, setSelectedId] = useState(id || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/streams?live=true`);
        if (cancel) return;
        setStreams(r.data || []);
        setLoading(false);
      } catch (_) { setLoading(false); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  useEffect(() => { if (id) setSelectedId(id); }, [id]);

  const selected = useMemo(() => {
    const liveStreams = streams.filter((s) => s.live);
    if (!liveStreams.length) return null;
    return liveStreams.find((s) => s.id === selectedId) || liveStreams[0];
  }, [streams, selectedId]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6" data-testid="watch-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] mono uppercase tracking-widest text-red-400 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot" /> Live Broadcast
          </div>
          <h1 className="display-italic text-4xl sm:text-5xl mt-1 uppercase sd-title-gradient">On-air storm coverage</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">
            Watch the 24/7 Storm Desk radar broadcast plus every live chaser feed as it comes on air.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {SOCIALS.filter((s) => s.key === "youtube").map((s) => (
            <a key={s.key} href={s.href} target="_blank" rel="noreferrer noopener" data-testid="watch-youtube-channel-link">
              <Button className="bg-red-600 hover:bg-red-500 text-white h-10 gap-2 border-0">
                <Tv className="w-4 h-4" /> Watch on YouTube
              </Button>
            </a>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div>
          {loading ? (
            <div className="glass rounded-2xl p-10 text-center text-slate-400 text-sm" data-testid="watch-loading">
              Loading live feeds…
            </div>
          ) : selected ? (
            <>
              <StreamPlayer stream={selected} />
              <div className="mt-4">
                <h2 className="display-italic text-2xl uppercase text-white">{selected.title}</h2>
                <div className="text-sm text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                  {selected.chaser && <span>{selected.chaser}</span>}
                  {selected.location && (
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {selected.location}</span>
                  )}
                  <span className="inline-flex items-center gap-1 mono text-[11px]">{kindLabel(selected.kind)}</span>
                </div>
              </div>
              {streams.filter((s) => s.live).length > 1 && (
                <div className="mt-6">
                  <div className="text-[11px] mono uppercase tracking-widest text-slate-400 mb-2">More live feeds</div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {streams.filter((s) => s.live && s.id !== selected?.id).map((s) => (
                      <StreamCard key={s.id} s={s} onClick={() => setSelectedId(s.id)} />
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-8">
                <div className="text-[11px] mono uppercase tracking-widest text-slate-400 mb-2">Also on air</div>
                <RadarProgram />
              </div>
            </>
          ) : (
            <>
              <div className="sd-panel rounded-2xl px-5 py-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="watch-offair-notice">
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Chaser video</div>
                  <div className="text-slate-100 font-semibold text-sm mt-0.5">OFF AIR — no live chaser feed configured</div>
                </div>
                <span className="mono text-[10px] uppercase tracking-widest text-slate-500">Radar broadcast is live below</span>
              </div>
              <RadarProgram />
            </>
          )}
        </div>

        <ChatPanel className="h-[560px] lg:h-[calc(100vh-160px)] lg:sticky lg:top-32" />
      </div>
    </div>
  );
};

export default Watch;
